import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import type { ActionHandlerName, ActionId } from "@/lib/actions/types/action-names";
import type { DetailActionStrict, ActionVariant } from "../../types/detail-action";
import { ACTION_ICONS, ACTION_VARIANTS, ROLE_COMBINATIONS } from "../../types/action-properties";
import { TEMPLATE_IDS } from "@/lib/constants/template-ids";

export function createCallAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "call",
    label: options.label ?? "Call",
    icon: options.icon ?? ACTION_ICONS.PHONE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "initiateCall",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}

export function createSendTextMessageAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "send-text-message",
    label: options.label ?? "Send Text",
    icon: options.icon ?? ACTION_ICONS.MESSAGE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "sendTextMessage",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}

export function createGenerateContractAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "generate-contract",
    label: options.label ?? "Generate Contract",
    icon: options.icon ?? ACTION_ICONS.FILE_TEXT,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "generateContract",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
  };
}

export function createSmAgreementAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "create-sm-agreement",
    label: options.label ?? "Create SM Agreement",
    icon: options.icon ?? ACTION_ICONS.FILE_TEXT,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    href: (item: T) =>
      `/contracts/template/${TEMPLATE_IDS.COST_ANALYSIS_OPTIMIZATION}?contact_id=${item.id}`,
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}

export function createGrantAgreementAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "create-grant-agreement",
    label: options.label ?? "Create Grant Agreement",
    icon: options.icon ?? ACTION_ICONS.FILE_TEXT,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    href: (item: T) =>
      `/contracts/template/${TEMPLATE_IDS.GRANTS_ANALYSIS_SERVICE_AGREEMENT}?contact_id=${item.id}`,
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}

export function createFillChecklistsAction<T extends BaseEntity>(
  options: {
    id?: ActionId;
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    action?: ActionHandlerName;
    allowedRoles?: string[];
    primary?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: options.id ?? "fill-both-checklists",
    label: options.label ?? "Fill Both Checklists",
    icon: options.icon ?? ACTION_ICONS.FILE_TEXT,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: options.action ?? "fillBothChecklists",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
    primary: options.primary ?? false,
  };
}

export function createManageAccessAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    bulkable?: boolean;
  } = {}
): DetailActionStrict<T> {
  return {
    id: "grant-employee-access",
    label: options.label ?? "Manage Access",
    icon: options.icon ?? ACTION_ICONS.SHIELD,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "grantEmployeeAccess",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
    bulkable: options.bulkable ?? true,
  };
}

export function createConsolidateAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
    tooltip?: string;
    action?: ActionHandlerName;
    id?: ActionId;
  } = {}
): DetailActionStrict<T> {
  return {
    id: options.id ?? "consolidate-contact",
    label: options.label ?? "Consolidate",
    icon: options.icon ?? ACTION_ICONS.GIT_MERGE,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: options.action ?? "consolidateContact",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.ADMIN_ONLY],
    tooltip: options.tooltip ?? "Merge this item into another",
  };
}

export function createResearchCompanyAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "research-company",
    label: options.label ?? "Research Company",
    icon: options.icon ?? ACTION_ICONS.SEARCH,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "openResearchCompanyModal",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}

export function createFindDecisionMakersAction<T extends BaseEntity>(
  options: {
    label?: string;
    icon?: string;
    variant?: ActionVariant;
    allowedRoles?: string[];
  } = {}
): DetailActionStrict<T> {
  return {
    id: "find-decision-makers",
    label: options.label ?? "Find Decision Makers",
    icon: options.icon ?? ACTION_ICONS.USERS,
    variant: options.variant ?? ACTION_VARIANTS.OUTLINE,
    action: "openFindDecisionMakersModal",
    allowedRoles: options.allowedRoles ?? [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  };
}
